import { useState, useEffect } from "react";
import { Formik, Form, Field, FieldArray } from "formik";
import { toast } from "react-toastify";
import { FiEdit, FiInfo, FiTrash2, FiX } from "react-icons/fi";
import "./JobApplications.css";
import AxiosWeb from "../../../utils/axiosWeb";

const JobApplications = () => {
  const [jobApplications, setJobApplications] = useState([]);
  const [filteredJobApplications, setFilteredJobApplications] = useState([]);
  const [jobTitles, setJobTitles] = useState({});
  const [jobPostings, setJobPostings] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingJobPostings, setIsLoadingJobPostings] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [applicationToDelete, setApplicationToDelete] = useState(null);
  const [applicationToEdit, setApplicationToEdit] = useState(null);
  const [selectedJobPostingId, setSelectedJobPostingId] = useState("");
  const [selectedJobTitle, setSelectedJobTitle] = useState("");
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);

  const applicationsPerPage = 5;
  const formStatusOptions = [
    "New",
    "Reviewed",
    "Interview Scheduled",
    "Rejected",
    "Hired",
  ];

  // ✅ Fetch all job postings for dropdown
  const fetchAllJobPostings = async () => {
  try {
    setIsLoadingJobPostings(true);

    const response = await AxiosWeb.get(
      `${process.env.REACT_APP_BASE_URL}/jobPosting/getAllJobPostings`
    );

    console.log("Job Posting API Response:", response.data);

    // Support different API response formats
    let postings = [];

    if (Array.isArray(response.data)) {
      postings = response.data;
    } else if (Array.isArray(response.data.data)) {
      postings = response.data.data;
    } else if (Array.isArray(response.data.content)) {
      postings = response.data.content;
    }

    // Transform data
    const formattedPostings = postings.map((posting) => ({
      ...posting,
      id: posting.id,
      title: posting.jobTitle,
    }));

    setJobPostings(formattedPostings);

    const titles = {};

    formattedPostings.forEach((posting) => {
      titles[posting.id] = posting.title;
    });

    setJobTitles(titles);
  } catch (error) {
    console.error("Error fetching job postings:", error);
    toast.error("Error loading job postings");
    setJobPostings([]);
  } finally {
    setIsLoadingJobPostings(false);
  }
};
  // Fetch job applications by Job Posting ID
const fetchJobApplicationsByPostingId = async (jobPostingId) => {

    if (!jobPostingId) {
        setJobApplications([]);
        setFilteredJobApplications([]);
        return;
    }

    try {

        setIsLoading(true);

        const response = await AxiosWeb.get(
            `${process.env.REACT_APP_BASE_URL}/jobApplication/getAllJobApplicationsByJobPostingID`,
            {
                params: {
                    jobPostingId: Number(jobPostingId)
                }
            }
        );

        console.log("Applications Response:", response);

        let applications = [];

        if (Array.isArray(response.data)) {
            applications = response.data;
        }
        else if (Array.isArray(response.data.data)) {
            applications = response.data.data;
        }
        else if (Array.isArray(response.data.content)) {
            applications = response.data.content;
        }
        else if (response.data.data && Array.isArray(response.data.data.content)) {
            applications = response.data.data.content;
        }

        console.log("Applications:", applications);

        setJobApplications(applications);
        setFilteredJobApplications(applications);

    }
    catch (err) {

        console.error(err);

        setJobApplications([]);
        setFilteredJobApplications([]);

    }
    finally {

        setIsLoading(false);

    }

};

  const getJobTitle = (application) => {
  // If a job posting is selected, show its ID
  if (selectedJobPostingId) {
    return selectedJobPostingId;
  }

  // Otherwise use the ID from the application
  if (application?.jobPosting?.id) {
    return application.jobPosting.id;
  }

  return "N/A";
};


  const getSelectedJobTitle = () => {
    if (!selectedJobPostingId) return "";
    return selectedJobTitle || `${selectedJobPostingId}`;
  };

  const resolveAttachmentUrl = (application, attachmentType) => {
    if (!application) return null;
    const relative = {
      resume: application.resumeUrl,
      coverletter: application.coverLetterUrl,
      photo: application.photoUrl,
    }[attachmentType];
    if (!relative) return null;
    if (relative.startsWith("http://") || relative.startsWith("https://")) {
      return relative;
    }
    return `${process.env.REACT_APP_BASE_URL}${relative}`;
  };

  const handleDownloadAttachment = async (applicationId, attachmentType) => {
    try {
      const application = jobApplications.find(
        (app) => app.id === applicationId
      );
      if (!application) throw new Error("Application not found");

      const url = resolveAttachmentUrl(application, attachmentType);
      if (!url) {
        throw new Error(`No ${attachmentType} available for this application`);
      }

      const response = await AxiosWeb.get(url, { responseType: "blob" });
      const contentType =
        response.headers["content-type"] || "application/octet-stream";
      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);

      const fileName = {
        resume: `Resume_${application.firstName}_${application.lastName}_${applicationId}`,
        coverletter: `CoverLetter_${application.firstName}_${application.lastName}_${applicationId}`,
        photo: `Photo_${application.firstName}_${application.lastName}_${applicationId}`,
      }[attachmentType];

      if (attachmentType === "photo") {
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
        toast.success("Photo downloaded successfully");
      } else {
        window.open(blobUrl, "_blank");
        toast.success(`${attachmentType} opened in new tab`);
      }
    } catch (error) {
      console.error(`Error downloading ${attachmentType}:`, error);
      toast.warn(`No ${attachmentType} available`);
    }
  };

  const handleEditApplication = (application) => {
    setApplicationToEdit(application);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (values, { setSubmitting }) => {
    try {
      setIsLoading(true);

      const response = await AxiosWeb.put(
        `${process.env.REACT_APP_BASE_URL}/jobApplication/updateJobApplication?id=${applicationToEdit.id}`,
        values
      );

      setShowEditModal(false);
      if (response.status === 200) {
        setJobApplications((prev) =>
          prev.map((app) =>
            app.id === applicationToEdit.id ? { ...app, ...values } : app
          )
        );
        toast.success("Application updated successfully!");
        await fetchAllJobPostings();
        setApplicationToEdit(null);
      }
    } catch (error) {
      console.error("Error updating application:", error);
      toast.error("Error updating application");
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  const confirmDelete = (application) => {
    setApplicationToDelete(application);
    setShowDeleteModal(true);
  };

  const handleDeleteApplication = async () => {
    if (!applicationToDelete) return;
    try {
      setIsLoading(true);
      await AxiosWeb.delete(
        `${process.env.REACT_APP_BASE_URL}/jobApplication/deleteJobApplication`,
        { params: { id: applicationToDelete.id } } // pass id as query param
      );

      setJobApplications((prev) =>
        prev.filter((app) => app.id !== applicationToDelete.id)
      );

      toast.success("Job application deleted successfully!");
      await fetchAllJobPostings();
      setShowDeleteModal(false);
      setApplicationToDelete(null);
    } catch (error) {
      toast.error("Error deleting job application");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusSubmit = async (values, { setSubmitting }) => {
    const updatePromises = Object.entries(values.status).map(
      async ([id, newStatus]) => {
        const originalStatus = jobApplications.find(
          (app) => app.id.toString() === id
        )?.status;
        if (newStatus !== originalStatus) {
          try {
            const res = await AxiosWeb.put(
              `${process.env.REACT_APP_BASE_URL}/jobApplication/updateApplication/${id}`,
              { status: newStatus },
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );
            if (res.status === 200) {
              setJobApplications((prev) =>
                prev.map((app) =>
                  app.id.toString() === id ? { ...app, status: newStatus } : app
                )
              );
            }
          } catch {
            toast.error(`Failed to update application ${id}`);
          }
        }
      }
    );
    await Promise.all(updatePromises);
    toast.success("Statuses updated successfully");
    setSubmitting(false);
  };

  const handleJobPostingChange = (e) => {
    const jobPostingId = e.target.value;
    setSelectedJobPostingId(jobPostingId);

    // Reset selected job title when changing job posting
    setSelectedJobTitle("");

    // Auto-fetch applications when job posting is selected
    if (jobPostingId) {
      fetchJobApplicationsByPostingId(jobPostingId);
    } else {
      setJobApplications([]);
      setFilteredJobApplications([]);
      setSelectedJobTitle("");
    }
  };

  useEffect(() => {
    fetchAllJobPostings();
  }, []);

  useEffect(() => {
    let objectUrl;
    const loadPhoto = async () => {
      if (!isInfoModalOpen || !selectedApplication?.photoUrl) {
        setPhotoPreviewUrl(null);
        return;
      }
      try {
        const url = resolveAttachmentUrl(selectedApplication, "photo");
        const response = await AxiosWeb.get(url, { responseType: "blob" });
        objectUrl = window.URL.createObjectURL(response.data);
        setPhotoPreviewUrl(objectUrl);
      } catch (error) {
        console.error("Error loading applicant photo:", error);
        setPhotoPreviewUrl(null);
      }
    };
    loadPhoto();
    return () => {
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [isInfoModalOpen, selectedApplication]);

  useEffect(() => {
    const nonDeleted = jobApplications.filter((app) => !app.deleted);
    const lowerSearch = searchTerm.toLowerCase();
    const searched = nonDeleted.filter(
      (app) =>
        (getJobTitle(app)?.toLowerCase() || "").includes(lowerSearch) ||
        (app.firstName?.toLowerCase() || "").includes(lowerSearch) ||
        (app.lastName?.toLowerCase() || "").includes(lowerSearch) ||
        (app.emailAddress?.toLowerCase() || "").includes(lowerSearch) ||
        (app.phoneNumber?.toLowerCase() || "").includes(lowerSearch)
    );
    const sorted = searched.sort(
      (a, b) => new Date(b.applicationDate) - new Date(a.applicationDate)
    );
    setFilteredJobApplications(sorted);
    setCurrentPage(1);
  }, [jobApplications, searchTerm, jobTitles, selectedJobTitle]);

  const indexOfLast = currentPage * applicationsPerPage;
  const indexOfFirst = indexOfLast - applicationsPerPage;
  const currentApps = filteredJobApplications.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(
    filteredJobApplications.length / applicationsPerPage
  );
  const paginate = (p) => setCurrentPage(p);

  const initialValues = {
    status: Object.fromEntries(
      jobApplications.map((app) => [app.id, app.status || "New"])
    ),
  };

  const handleInfo = (app) => {
    setSelectedApplication(app);
    setIsInfoModalOpen(true);
  };

  return (
    <div className="job-applications-container">
      <div className="job-posting-selector" style={{ marginBottom: "12px" }}>
        <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label htmlFor="jobPostingSelect" style={{ color: "#2a4e85" }}>
              Select Job Posting *
            </label>
            <select
              id="jobPostingSelect"
              value={selectedJobPostingId}
              onChange={handleJobPostingChange}
              className="form-input"
              style={{ width: "100%" }}
              disabled={isLoadingJobPostings}
            >
              <option value="">Choose a job posting...</option>
              {jobPostings.map((posting) => (
                <option key={posting.id} value={posting.id}>
                  {posting.title || `Job ID : ${posting.id}`}
                  {posting.jobTitle ? ` - ${posting.jobTitle}` : ""}
                  {posting.location ? ` (${posting.location})` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>
        {selectedJobPostingId && (
          <div
            style={{ marginTop: "8px", color: "#2a4e85", fontWeight: "500" }}
          >
            Selected Job: <strong>{getSelectedJobTitle()}</strong>
            {jobApplications.length > 0 && (
              <span
                style={{ marginLeft: "15px", color: "#666", fontSize: "14px" }}
              >
                {jobApplications.length} application found
              </span>
            )}
          </div>
        )}
        {isLoadingJobPostings && (
          <div style={{ marginTop: "8px", color: "#666", fontSize: "14px" }}>
            Loading job postings...
          </div>
        )}
      </div>

      <div className="applications-list-section">
        <h3>Job Applications ({filteredJobApplications.length} total)</h3>

        {!selectedJobPostingId ? (
          <div className="no-applications">
            Please select a job posting to view applications
          </div>
        ) : isLoading ? (
          <div className="loading">Loading job applications...</div>
        ) : filteredJobApplications.length === 0 ? (
          <p className="no-applications">
            No job applications found for this job posting.
          </p>
        ) : (
          <Formik
            initialValues={initialValues}
            onSubmit={handleStatusSubmit}
            enableReinitialize
          >
            {({ isSubmitting }) => (
              <Form>
                <div className="applications-table-container">
                  <table className="applications-table">
                    <thead>
                      <tr>
                        <th>Job ID</th>
                        <th>Candidate</th>
                        <th>Phone</th>
                        <th>Email</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Attachments</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentApps.map((app) => (
                           <tr key={app.id}>         
                           <td>
                            <strong>{getJobTitle(app)}</strong>
                          </td>
                          <td>{`${app.firstName || ""} ${
                            app.lastName || ""
                          }`}</td>
                          <td>{app.phoneNumber || "N/A"}</td>
                          <td>{app.emailAddress || "N/A"}</td>
                          <td>
                            {new Date(app.applicationDate).toLocaleDateString()}
                          </td>
                          <td>
                            <Field
                              as="select"
                              name={`status.${app.id}`}
                              className="status-select"
                            >
                              {formStatusOptions.map((status) => (
                                <option key={status} value={status}>
                                  {status}
                                </option>
                              ))}
                            </Field>
                          </td>
                          <td>
                            <div className="attachment-buttons">
                              <button
                                type="button"
                                onClick={() =>
                                  handleDownloadAttachment(app.id, "resume")
                                }
                                className="attachment-btn"
                                disabled={!app.resumeUrl}
                              >
                                Resume
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleDownloadAttachment(
                                    app.id,
                                    "coverletter"
                                  )
                                }
                                className="attachment-btn"
                                disabled={!app.coverLetterUrl}
                              >
                                Cover Letter
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleDownloadAttachment(app.id, "photo")
                                }
                                className="attachment-btn"
                                disabled={!app.photoUrl}
                              >
                                Photo
                              </button>
                            </div>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                type="button"
                                className="icon-btn info-btn"
                                onClick={() => handleInfo(app)}
                              >
                                <FiInfo size={18} />
                              </button>
                              <br />
                              <button
                                type="button"
                                className="icon-btn edit-btn"
                                onClick={() => handleEditApplication(app)}
                              >
                                <FiEdit size={18} />
                              </button>
                              <br />
                              <button
                                type="button"
                                className="icon-btn delete-btn"
                                onClick={() => confirmDelete(app)}
                              >
                                <FiTrash2 size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="pagination">
                  <button
                    type="button"
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>

      {/* Infor */}
      {isInfoModalOpen && selectedApplication && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            {/* Modal Header */}
            <div className="modal-header">
              <h2 style={{ color: "#2a4e85" }}>Application Details</h2>
              <button
                className="modal-close-btn"
                onClick={() => setIsInfoModalOpen(false)}
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="modal-body">
              <h3 style={{ color: "#2a4e85" }}>Candidate Information</h3>
              {photoPreviewUrl && (
                <div className="applicant-photo-preview">
                  <img src={photoPreviewUrl} alt="Applicant" />
                </div>
              )}
              <div className="form-grid">
                <div className="form-group">
                  <label style={{ color: "#2a4e85" }}>Name</label>
                  <input
                    type="text"
                    value={`${selectedApplication.prefix} ${selectedApplication.firstName} ${selectedApplication.lastName}`}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>

                <div className="form-group">
                  <label style={{ color: "#2a4e85" }}>Email ID</label>
                  <input
                    type="text"
                    value={selectedApplication.emailAddress}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>

                <div className="form-group">
                  <label style={{ color: "#2a4e85" }}>Phone Number</label>
                  <input
                    type="text"
                    value={`${selectedApplication.countryCode} ${selectedApplication.phoneNumber}`}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>
                <div className="form-group">
                  <label style={{ color: "#2a4e85" }}>Date of Birth</label>
                  <input
                    type="text"
                    value={`${selectedApplication.dateOfBirth} `}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>

                <div className="form-group">
                  <label style={{ color: "#2a4e85" }}>Gender</label>
                  <input
                    type="text"
                    value={selectedApplication.gender || "N/A"}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>

                <div className="form-group">
                  <label style={{ color: "#2a4e85" }}>Marital Status</label>
                  <input
                    type="text"
                    value={selectedApplication.maritalStatus || "N/A"}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>

                <div className="form-group">
                  <label style={{ color: "#2a4e85" }}>Nationality</label>
                  <input
                    type="text"
                    value={selectedApplication.nationality || "N/A"}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>

                <div className="form-group">
                  <label style={{ color: "#2a4e85" }}>
                    Alternate Country Code
                  </label>
                  <input
                    type="text"
                    value={selectedApplication.countryCode2 || "N/A"}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>

                <div className="form-group">
                  <label style={{ color: "#2a4e85" }}>
                    Alternate Phone Number
                  </label>
                  <input
                    type="text"
                    value={selectedApplication.phoneNumber2 || "N/A"}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>

                <div className="form-group">
                  <label style={{ color: "#2a4e85" }}>Alternate Email</label>
                  <input
                    type="text"
                    value={selectedApplication.emailAddress2 || "N/A"}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>

                {/* Permanent Address Section */}
                <div className="form-group full">
                  <label style={{ color: "#2a4e85" }}>
                    Permanent Address Line 1
                  </label>
                  <input
                    type="text"
                    value={selectedApplication.permanentAddressLine1 || "N/A"}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>

                <div className="form-group full">
                  <label style={{ color: "#2a4e85" }}>
                    Permanent Address Line 2
                  </label>
                  <input
                    type="text"
                    value={selectedApplication.permanentAddressLine2 || "N/A"}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>

                <div className="form-group">
                  <label style={{ color: "#2a4e85" }}>Permanent City</label>
                  <input
                    type="text"
                    value={selectedApplication.permanentCity || "N/A"}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>

                <div className="form-group">
                  <label style={{ color: "#2a4e85" }}>Permanent State</label>
                  <input
                    type="text"
                    value={selectedApplication.permanentState || "N/A"}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>

                <div className="form-group">
                  <label style={{ color: "#2a4e85" }}>
                    Permanent Postal Code
                  </label>
                  <input
                    type="text"
                    value={selectedApplication.permanentPostalCode || "N/A"}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>

                <div className="form-group">
                  <label style={{ color: "#2a4e85" }}>Permanent Country</label>
                  <input
                    type="text"
                    value={selectedApplication.permanentCountry || "N/A"}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>

                <div className="form-group">
                  <label style={{ color: "#2a4e85" }}>Created By</label>
                  <input
                    type="text"
                    value={selectedApplication.createdBy || "N/A"}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>

                <div className="form-group full">
                  <label style={{ color: "#2a4e85" }}>Current Address</label>
                  <input
                    type="text"
                    value={`${selectedApplication.addressLine1}`}
                    readOnly
                    style={{
                      backgroundColor: "#fafafa",
                      color: "black",
                      marginBottom: "8px",
                    }}
                  />
                  <input
                    type="text"
                    value={`${selectedApplication.addressLine2}`}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>
                <div className="form-group full">
                  <label style={{ color: "#2a4e85" }}>City</label>
                  <input
                    type="text"
                    value={` ${selectedApplication.city}  `}
                    readOnly
                    style={{
                      backgroundColor: "#fafafa",
                      color: "black",
                      marginBottom: "8px",
                    }}
                  />

                  <div className="form-group full">
                    <label style={{ color: "#2a4e85" }}>State</label>
                    <input
                      type="text"
                      value={` ${selectedApplication.state} `}
                      readOnly
                      style={{ backgroundColor: "#fafafa", color: "black" }}
                    />
                  </div>
                  <div className="form-group full">
                    <label style={{ color: "#2a4e85" }}>Postal Code</label>
                    <input
                      type="text"
                      value={` ${selectedApplication.postalCode} `}
                      readOnly
                      style={{ backgroundColor: "#fafafa", color: "black" }}
                    />
                  </div>
                  <div className="form-group full">
                    <label style={{ color: "#2a4e85" }}>Country</label>
                    <input
                      type="text"
                      value={` ${selectedApplication.country} `}
                      readOnly
                      style={{ backgroundColor: "#fafafa", color: "black" }}
                    />
                  </div>
                </div>
              </div>

              {/* Qualifications */}
              <h3 style={{ color: "#2a4e85" }}>Qualifications</h3>
              {selectedApplication.qualifications?.map((q, i) => (
                <div key={i} className="box-section">
                  <div className="form-grid">
                    <div className="form-group">
                      <label style={{ color: "#2a4e85" }}>Degree</label>
                      <input
                        type="text"
                        value={q.degree}
                        readOnly
                        style={{ backgroundColor: "#fafafa", color: "black" }}
                      />
                    </div>
                    <div className="form-group">
                      <label style={{ color: "#2a4e85" }}>Major</label>
                      <input
                        type="text"
                        value={q.major}
                        readOnly
                        style={{ backgroundColor: "#fafafa", color: "black" }}
                      />
                    </div>
                    <div className="form-group">
                      <label style={{ color: "#2a4e85" }}>Minor</label>
                      <input
                        type="text"
                        value={q.minor}
                        readOnly
                        style={{ backgroundColor: "#fafafa", color: "black" }}
                      />
                    </div>

                    <div className="form-group">
                      <label style={{ color: "#2a4e85" }}>Institution</label>
                      <input
                        type="text"
                        value={q.institution}
                        readOnly
                        style={{ backgroundColor: "#fafafa", color: "black" }}
                      />
                    </div>
                    <div className="form-group">
                      <label style={{ color: "#2a4e85" }}>Universtiy</label>
                      <input
                        type="text"
                        value={q.university}
                        readOnly
                        style={{ backgroundColor: "#fafafa", color: "black" }}
                      />
                    </div>
                    <div className="form-group">
                      <label style={{ color: "#2a4e85" }}>Location</label>
                      <input
                        type="text"
                        value={q.location}
                        readOnly
                        style={{ backgroundColor: "#fafafa", color: "black" }}
                      />
                    </div>

                    <div className="form-group">
                      <label style={{ color: "#2a4e85" }}>CGPA</label>
                      <input
                        type="text"
                        value={q.cgpa}
                        readOnly
                        style={{ backgroundColor: "#fafafa", color: "black" }}
                      />
                    </div>

                    <div className="form-group">
                      <label style={{ color: "#2a4e85" }}>Duration</label>
                      <input
                        type="text"
                        value={`From : ${q.startDate} --→ To : ${q.endDate}`}
                        readOnly
                        style={{ backgroundColor: "#fafafa", color: "black" }}
                      />
                    </div>
                  </div>
                </div>
              ))}

              {/* Job Applied */}
              <h3 style={{ color: "#2a4e85" }}>Job Applied</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Job Title</label>
                  <input
                    type="text"
                    value={selectedApplication.jobPosting?.jobTitle}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>

                <div className="form-group">
                  <label style={{ color: "#2a4e85" }}>Department</label>
                  <input
                    type="text"
                    value={selectedApplication.jobPosting?.department}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>

                <div className="form-group">
                  <label style={{ color: "#2a4e85" }}>Location</label>
                  <input
                    type="text"
                    value={selectedApplication.jobPosting?.location}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>

                <div className="form-group">
                  <label style={{ color: "#2a4e85" }}>Job Type</label>
                  <input
                    type="text"
                    value={selectedApplication.jobPosting?.type}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>

                <div className="form-group full">
                  <label style={{ color: "#2a4e85" }}>
                    Experience Required
                  </label>
                  <input
                    type="text"
                    value={selectedApplication.jobPosting?.experience}
                    readOnly
                    style={{ backgroundColor: "#fafafa", color: "black" }}
                  />
                </div>
              </div>

              <h3 style={{ color: "#2a4e85" }}>Work Experience</h3>
              {selectedApplication.workExperience?.map((w, i) => (
                <div key={i} className="box-section">
                  <div className="form-grid">
                    <div className="form-group">
                      <label style={{ color: "#2a4e85" }}>Job Title</label>
                      <input
                        type="text"
                        value={w.jobTitle || "N/A"}
                        readOnly
                        style={{ backgroundColor: "#fafafa", color: "black" }}
                      />
                    </div>

                    <div className="form-group">
                      <label style={{ color: "#2a4e85" }}>Company Name</label>
                      <input
                        type="text"
                        value={w.companyName || "N/A"}
                        readOnly
                        style={{ backgroundColor: "#fafafa", color: "black" }}
                      />
                    </div>
                    <div className="form-group">
                      <label style={{ color: "#2a4e85" }}>
                        Company Location
                      </label>
                      <input
                        type="text"
                        value={w.companyLocation || "N/A"}
                        readOnly
                        style={{ backgroundColor: "#fafafa", color: "black" }}
                      />
                    </div>

                    <div className="form-group">
                      <label style={{ color: "#2a4e85" }}>Duration</label>
                      <input
                        type="text"
                        value={`From : ${w?.startDate || "N/A"} --→ To : ${
                          w?.endDate || "N/A"
                        } `}
                        readOnly
                        style={{ backgroundColor: "#fafafa", color: "black" }}
                      />
                    </div>

                    <div className="form-group full">
                      <label style={{ color: "#2a4e85" }}>Achievements</label>
                      <textarea
                        value={w.achievements || "N/A"}
                        readOnly
                        rows={3}
                        style={{ backgroundColor: "#fafafa", color: "black" }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showEditModal && applicationToEdit && (
        <div className="modal-overlay">
          <div className="modal-content large-modal">
            {/* Modal Header */}
            <div className="modal-header">
              <h2 style={{ color: "#2a4e85" }}>Edit Application</h2>
              <button
                className="modal-close-btn"
                onClick={() => setShowEditModal(false)}
              >
                <FiX size={24} />
              </button>
            </div>

            <Formik
              initialValues={{
                ...applicationToEdit,
                qualifications: applicationToEdit.qualifications || [],
                workExperience: applicationToEdit.workExperience || [],
              }}
              onSubmit={handleEditSubmit}
            >
              {({ values, setFieldValue }) => (
                <Form>
                  <div className="modal-body">
                    <h3 style={{ color: "#2a4e85" }}>Candidate Information</h3>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Name Prefix</label>
                        <Field
                          type="text"
                          name="prefix"
                          className="form-input"
                        />
                      </div>

                      <div className="form-group">
                        <label>First Name</label>
                        <Field
                          type="text"
                          name="firstName"
                          className="form-input"
                        />
                      </div>

                      <div className="form-group">
                        <label>Last Name</label>
                        <Field
                          type="text"
                          name="lastName"
                          className="form-input"
                        />
                      </div>

                      <div className="form-group">
                        <label>Email Address</label>
                        <Field
                          type="email"
                          name="emailAddress"
                          className="form-input"
                        />
                      </div>

                      <div className="form-group">
                        <label>Phone Number</label>
                        <Field
                          type="tel"
                          name="phoneNumber"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Date of Birth</label>
                        <Field
                          type="date"
                          name="dateOfBirth"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Gender</label>
                        <Field
                          type="text"
                          name="gender"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Marital Status</label>
                        <Field
                          type="text"
                          name="maritalStatus"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Nationality</label>
                        <Field
                          type="text"
                          name="nationality"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Alternate Country Code</label>
                        <Field
                          type="text"
                          name="countryCode2"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Alternate Phone Number</label>
                        <Field
                          type="tel"
                          name="phoneNumber2"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Alternate Email</label>
                        <Field
                          type="email"
                          name="emailAddress2"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>
                    </div>

                    <h3 style={{ color: "#2a4e85" }}>Permanent Address</h3>
                    <div className="form-grid">
                      <div className="form-group full">
                        <label>Address Line 1</label>
                        <Field
                          type="text"
                          name="permanentAddressLine1"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group full">
                        <label>Address Line 2</label>
                        <Field
                          type="text"
                          name="permanentAddressLine2"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>City</label>
                        <Field
                          type="text"
                          name="permanentCity"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>State</label>
                        <Field
                          type="text"
                          name="permanentState"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Postal Code</label>
                        <Field
                          type="text"
                          name="permanentPostalCode"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Country</label>
                        <Field
                          type="text"
                          name="permanentCountry"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>
                    </div>

                    <h3 style={{ color: "#2a4e85" }}>Current Address</h3>
                    <div className="form-grid">
                      <div className="form-group full">
                        <label>Address Line 1</label>
                        <Field
                          type="text"
                          name="addressLine1"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group full">
                        <label>Address Line 2</label>
                        <Field
                          type="text"
                          name="addressLine2"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>City</label>
                        <Field
                          type="text"
                          name="city"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>State</label>
                        <Field
                          type="text"
                          name="state"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Postal Code</label>
                        <Field
                          type="text"
                          name="postalCode"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Country</label>
                        <Field
                          type="text"
                          name="country"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>
                    </div>

                    <h3 style={{ color: "#2a4e85" }}>Qualifications</h3>

                    <FieldArray name="qualifications">
                      {({ push, remove }) => (
                        <>
                          {values.qualifications.map((q, i) => (
                            <div key={i} className="box-section">
                              <div className="form-grid">
                                <div className="form-group">
                                  <label>Degree</label>
                                  <Field
                                    name={`qualifications.${i}.degree`}
                                    className="form-input"
                                    style={{ color: "#2a4e85" }}
                                  />
                                </div>

                                <div className="form-group">
                                  <label>Major</label>
                                  <Field
                                    name={`qualifications.${i}.major`}
                                    className="form-input"
                                    style={{ color: "#2a4e85" }}
                                  />
                                </div>

                                <div className="form-group">
                                  <label>Minor</label>
                                  <Field
                                    name={`qualifications.${i}.minor`}
                                    className="form-input"
                                    style={{ color: "#2a4e85" }}
                                  />
                                </div>

                                <div className="form-group">
                                  <label>Institution</label>
                                  <Field
                                    name={`qualifications.${i}.institution`}
                                    className="form-input"
                                    style={{ color: "#2a4e85" }}
                                  />
                                </div>

                                <div className="form-group">
                                  <label>University</label>
                                  <Field
                                    name={`qualifications.${i}.university`}
                                    className="form-input"
                                    style={{ color: "#2a4e85" }}
                                  />
                                </div>

                                <div className="form-group">
                                  <label>Location</label>
                                  <Field
                                    name={`qualifications.${i}.location`}
                                    className="form-input"
                                    style={{ color: "#2a4e85" }}
                                  />
                                </div>

                                <div className="form-group">
                                  <label>CGPA</label>
                                  <Field
                                    name={`qualifications.${i}.cgpa`}
                                    className="form-input"
                                    style={{ color: "#2a4e85" }}
                                  />
                                </div>

                                <div className="form-group">
                                  <label>Start Date</label>
                                  <Field
                                    type="date"
                                    name={`qualifications.${i}.startDate`}
                                    className="form-input"
                                    style={{ color: "#2a4e85" }}
                                  />
                                </div>

                                <div className="form-group">
                                  <label>End Date</label>
                                  <Field
                                    type="date"
                                    name={`qualifications.${i}.endDate`}
                                    className="form-input"
                                    style={{ color: "#2a4e85" }}
                                  />
                                </div>

                                <button
                                  type="button"
                                  className="btn-add"
                                  onClick={() =>
                                    push({
                                      degree: "",
                                      major: "",
                                      minor: "",
                                      institution: "",
                                      university: "",
                                      location: "",
                                      cgpa: "",
                                      startDate: "",
                                      endDate: "",
                                    })
                                  }
                                  style={{
                                    backgroundColor: "green",
                                    color: "white",
                                    padding: "6px 1px",
                                    border: "none",
                                    borderRadius: "5px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                  }}
                                >
                                  + Add Qualification
                                </button>

                                <button
                                  type="button"
                                  onClick={() => remove(i)}
                                  style={{
                                    backgroundColor: "#ff4d4d",
                                    color: "white",
                                    padding: "6px 1px",
                                    border: "none",
                                    borderRadius: "5px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </FieldArray>

                    <h3 style={{ color: "#2a4e85", marginTop: "15px" }}>
                      Job Applied
                    </h3>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Job Title</label>
                        <Field
                          type="text"
                          name="jobPosting.jobTitle"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Department</label>
                        <Field
                          type="text"
                          name="jobPosting.department"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Location</label>
                        <Field
                          type="text"
                          name="jobPosting.location"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Type</label>
                        <Field
                          type="text"
                          name="jobPosting.type"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Experience Required</label>
                        <Field
                          type="text"
                          name="jobPosting.experience"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>
                    </div>

                    <h3 style={{ color: "#2a4e85", marginTop: "10px" }}>
                      Work Experience
                    </h3>

                    <FieldArray name="workExperience">
                      {({ push, remove }) => (
                        <>
                          {values.workExperience.map((w, i) => (
                            <div key={i} className="box-section">
                              <div className="form-grid">
                                <div className="form-group">
                                  <label>Job Title</label>
                                  <Field
                                    name={`workExperience.${i}.jobTitle`}
                                    className="form-input"
                                    style={{ color: "#2a4e85" }}
                                  />
                                </div>

                                <div className="form-group">
                                  <label>Company Name</label>
                                  <Field
                                    name={`workExperience.${i}.companyName`}
                                    className="form-input"
                                    style={{ color: "#2a4e85" }}
                                  />
                                </div>

                                <div className="form-group">
                                  <label>Company Location</label>
                                  <Field
                                    name={`workExperience.${i}.companyLocation`}
                                    className="form-input"
                                    style={{ color: "#2a4e85" }}
                                  />
                                </div>

                                <div className="form-group">
                                  <label>Start Date</label>
                                  <Field
                                    type="date"
                                    name={`workExperience.${i}.startDate`}
                                    className="form-input"
                                    style={{ color: "#2a4e85" }}
                                  />
                                </div>

                                <div className="form-group">
                                  <label>End Date</label>
                                  <Field
                                    type="date"
                                    name={`workExperience.${i}.endDate`}
                                    className="form-input"
                                    style={{ color: "#2a4e85" }}
                                  />
                                </div>

                                <div className="form-group full">
                                  <label>Achievements</label>
                                  <Field
                                    as="textarea"
                                    name={`workExperience.${i}.achievements`}
                                    rows="3"
                                    className="form-input"
                                    style={{ color: "#2a4e85" }}
                                  />
                                </div>

                                <button
                                  type="button"
                                  className="btn-add"
                                  onClick={() =>
                                    push({
                                      jobTitle: "",
                                      companyName: "",
                                      companyLocation: "",
                                      startDate: "",
                                      endDate: "",
                                      achievements: "",
                                    })
                                  }
                                  style={{
                                    backgroundColor: "green",
                                    color: "white",
                                    padding: "6px 1px",
                                    border: "none",
                                    borderRadius: "5px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                  }}
                                >
                                  + Add Work Experience
                                </button>

                                <button
                                  type="button"
                                  className="btn-delete"
                                  onClick={() => remove(i)}
                                  style={{
                                    backgroundColor: "#ff4d4d",
                                    color: "white",
                                    padding: "6px 1px",
                                    border: "none",
                                    borderRadius: "5px",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </FieldArray>

                    <h3 style={{ color: "#2a4e85", marginTop: "15px" }}>
                      System Details
                    </h3>
                    <div className="form-grid">
                      <div className="form-group">
                        <label>Created By</label>
                        <Field
                          name="createdBy"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Application Date</label>
                        <Field
                          type="date"
                          name="applicationDate"
                          className="form-input"
                          style={{ color: "#2a4e85" }}
                        />
                      </div>

                      <div className="form-group">
                        <label>Status</label>
                        <Field as="select" name="status" className="form-input">
                          {formStatusOptions.map((status) => (
                            <option
                              key={status}
                              value={status}
                              style={{ color: "#2a4e85" }}
                            >
                              {status}
                            </option>
                          ))}
                        </Field>
                      </div>

                      <button
                        type="button"
                        className="btn-cancel"
                        onClick={() => setShowEditModal(false)}
                        style={{ marginBlock: "15px" }}
                      >
                        Cancel
                      </button>

                      <button
                        type="submit"
                        className="btn-save"
                        style={{ marginBlock: "15px" }}
                      >
                        Update Application
                      </button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal-overlay">
          <div
            className="modal-content"
            style={{ maxWidth: "400px", textAlign: "center" }}
          >
            <h3 style={{ color: "#2a4e85", marginBottom: "15px" }}>
              Confirm Delete
            </h3>
            <p style={{ color: "#555", marginBottom: "25px" }}>
              Are you sure you want to delete this job application?
            </p>
            <div className="form-actions" style={{ justifyContent: "center" }}>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowDeleteModal(false)}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-save"
                style={{ backgroundColor: "#dc3545" }}
                onClick={handleDeleteApplication}
                disabled={isLoading}
              >
                {isLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobApplications;
